import { useState, useLayoutEffect, useRef } from 'react'

/**
 * Manages the loading / error UI for a lightbox <img>, driven by the real DOM
 * element rather than assuming every open/navigation starts a fresh network
 * load.
 *
 * Why: a browser-cached image can already be `complete` before its `onLoad`
 * can fire — and `onLoad` never fires again for an already-decoded image. Code
 * that blindly sets "loading = true" on open/navigate therefore leaves a
 * spinner that never clears. This hook probes `imgRef.current.complete` on each
 * change so a cached image shows no spinner, while an uncached one shows the
 * spinner until `onLoad`/`onError`.
 *
 * Runs as a layout effect so `imgRef` already points at the committed <img>
 * carrying the new `src`.
 *
 * `isLoading` / `hasError` are only meaningful while an <img> is actually
 * rendered. A consumer that also shows non-image items (e.g. a video iframe)
 * must pass `skip` for those and render the spinner/error UI only in its image
 * branch — the hook can't gate the consumer's JSX.
 *
 * @param {*} resetKey    value that changes whenever the displayed image changes
 *                        (e.g. `${isOpen}-${currentIndex}`); triggers a re-probe.
 * @param {boolean} skip  force not-loading (e.g. the current item is a video, so
 *                        no <img> is rendered).
 * @returns {{ isLoading: boolean, hasError: boolean, imgRef: React.RefObject, onLoad: Function, onError: Function }}
 */
export const useLightboxImageState = (resetKey, skip = false) => {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const imgRef = useRef(null)

  useLayoutEffect(() => {
    setHasError(false)
    if (skip) {
      setIsLoading(false)
      return
    }
    const node = imgRef.current
    const alreadyLoaded = !!node && node.complete && node.naturalWidth > 0
    setIsLoading(!alreadyLoaded)
  }, [resetKey, skip])

  const onLoad = () => {
    setIsLoading(false)
    setHasError(false)
  }

  const onError = () => {
    setIsLoading(false)
    setHasError(true)
  }

  return { isLoading, hasError, imgRef, onLoad, onError }
}
